/**
 * NameBlockR — structured name block (family_name, given_names, name_in_script).
 * Subfields defined in types.json#primitives.name_block.
 */

import { useFormContext } from 'react-hook-form';
import { TextField, Section } from '../../../components/intake/fields';
import type { FieldDef, RendererProps } from '../types';
import { resolveLabel, resolveHelpText, deriveFieldName } from '../utils';
import { lookupFieldTooltip } from '../../fieldTooltips';

interface Props extends RendererProps {
  field: FieldDef;
}

export function NameBlockR({ field, formId, pdfMode = false, namePrefix, audience }: Props) {
  const { getValues } = useFormContext();
  const baseName = deriveFieldName(field, namePrefix);
  const label = resolveLabel(field, formId, audience);

  const sub = (subfield: string) => `${baseName}.${subfield}`;

  if (pdfMode) {
    const v = getValues(baseName) as Record<string, string> | undefined ?? {};
    const display = [v.family_name, v.given_names].filter(Boolean).join(', ');
    return (
      <div>
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        <p className="text-sm text-slate-800 mt-0.5">
          {display || <span className="text-slate-400 italic">Not provided</span>}
          {v.name_in_script && (
            <span className="block text-xs text-slate-500 mt-0.5">({v.name_in_script})</span>
          )}
        </p>
      </div>
    );
  }

  return (
    <Section title={label} description={resolveHelpText(field, audience)}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextField
          name={sub('family_name')}
          label="Family name"
          required={field.required}
          placeholder="As in passport"
          tooltip={lookupFieldTooltip(`${field.canonical_name}.family_name`)}
        />
        <TextField
          name={sub('given_names')}
          label="Given names"
          required={field.required}
          placeholder="As in passport"
          tooltip={lookupFieldTooltip(`${field.canonical_name}.given_names`)}
        />
      </div>
      <TextField
        name={sub('name_in_script')}
        label="Name in own language / script"
        hint="Optional — leave blank if not applicable"
        tooltip={lookupFieldTooltip(`${field.canonical_name}.name_in_script`)}
      />
    </Section>
  );
}
